import { useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { userService, type User } from '@/services/userService'

interface ChangeRoleModalProps {
  currentUserRole: 'owner' | 'admin' | 'auditor' | 'security' | 'user'
  onSuccess?: () => void
}

export interface ChangeRoleModalRef {
  open: (user: User) => void
  close: () => void
}

const ChangeRoleModal = forwardRef<ChangeRoleModalRef, ChangeRoleModalProps>(
  ({ currentUserRole, onSuccess }, ref) => {
    const { t } = useTranslation('platformUsers')
    const [isOpen, setIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState(false)
    const [user, setUser] = useState<User | null>(null)
    const [selectedRole, setSelectedRole] = useState<'owner' | 'admin' | 'auditor' | 'security' | 'user'>('user')
    const [actionType, setActionType] = useState<'direct' | 'request'>('direct') // Only for owner

    useImperativeHandle(ref, () => ({
      open: (userData: User) => {
        setUser(userData)
        setSelectedRole(userData.platform_role)
        // Owner defaults to direct change, admin can only request
        setActionType(currentUserRole === 'owner' ? 'direct' : 'request')
        setIsOpen(true)
        setError('')
        setSuccess(false)
      },
      close: () => {
        setIsOpen(false)
        setUser(null)
        setSelectedRole('user')
        setActionType('direct')
        setError('')
        setSuccess(false)
      },
    }))

    const isOwner = currentUserRole === 'owner'

    const handleSubmit = async () => {
      if (!user || loading) return

      try {
        setLoading(true)
        setError('')

        if (actionType === 'direct') {
          // Owner直接修改
          await userService.updateUserRole(user.id, selectedRole)
        } else {
          // 提交請求 (Admin或Owner選擇request方式)
          await userService.requestUserRoleChange(user.id, selectedRole)
        }

        // Show success message instead of closing immediately
        setSuccess(true)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
        if (errorMessage.startsWith('errors.')) {
          setError(t(errorMessage, errorMessage))
        } else {
          setError(errorMessage)
        }
      } finally {
        setLoading(false)
      }
    }

    const handleClose = () => {
      const wasSuccess = success
      setIsOpen(false)
      setUser(null)
      setSelectedRole('user')
      setActionType('direct')
      setError('')
      setSuccess(false)

      // Only call onSuccess if the operation was successful
      if (wasSuccess) {
        onSuccess?.()
      }
    }

    const getRoleColor = (role: string) => {
      switch (role) {
        case 'owner':
          return 'bg-purple-100 text-purple-800 border-purple-200'
        case 'admin':
          return 'bg-indigo-100 text-indigo-800 border-indigo-200'
        case 'auditor':
          return 'bg-amber-100 text-amber-800 border-amber-200'
        case 'security':
          return 'bg-red-100 text-red-800 border-red-200'
        case 'user':
          return 'bg-blue-100 text-blue-800 border-blue-200'
        default:
          return 'bg-gray-100 text-gray-800 border-gray-200'
      }
    }

    if (!isOpen || !user) return null

    const canSubmit = selectedRole !== user.platform_role && !loading

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white/30 backdrop-blur-md">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-purple-600">
                  {t('changeRoleModal.title', 'Change User Role')}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t('changeRoleModal.subtitle', 'Modify user permissions')}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={loading}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Success Message */}
            {success && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-900 mb-1">
                      {actionType === 'direct'
                        ? t('changeRoleModal.successDirect', 'Role changed successfully!')
                        : t('changeRoleModal.successRequest', 'Role change request submitted successfully!')
                      }
                    </p>
                    <p className="text-xs text-green-700">
                      {actionType === 'direct'
                        ? t('changeRoleModal.successDirectDesc', 'The user role has been updated immediately.')
                        : t('changeRoleModal.successRequestDesc', 'The request is pending owner approval.')
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && !success && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* User Info */}
            {!success && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3">
                  {t('changeRoleModal.message', 'Change role for the following user:')}
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{t('table.account', 'Account')}:</span>
                    <span className="text-sm text-gray-900">{user.account}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{t('table.name', 'Name')}:</span>
                    <span className="text-sm text-gray-900">{user.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{t('changeRoleModal.currentRole', 'Current Role')}:</span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getRoleColor(user.platform_role)}`}>
                      {t(`roles.${user.platform_role}`, user.platform_role)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Type Selection (Only for Owner) */}
            {!success && isOwner && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('changeRoleModal.actionType', 'Action Type')}
                </label>
                <div className="flex gap-3">
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="direct"
                      checked={actionType === 'direct'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={loading}
                      className="sr-only"
                    />
                    <div className={`px-4 py-3 border-2 rounded-lg transition-all ${
                      actionType === 'direct'
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <span className="text-sm font-medium text-gray-900 block">
                        {t('changeRoleModal.directChange', 'Direct Change')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('changeRoleModal.directChangeDesc', 'Immediately apply the role change')}
                      </p>
                    </div>
                  </label>
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="request"
                      checked={actionType === 'request'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={loading}
                      className="sr-only"
                    />
                    <div className={`px-4 py-3 border-2 rounded-lg transition-all ${
                      actionType === 'request'
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <span className="text-sm font-medium text-gray-900 block">
                        {t('changeRoleModal.submitRequest', 'Submit Request')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('changeRoleModal.submitRequestDesc', 'Create a role change request')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Admin Info Notice */}
            {!success && !isOwner && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-blue-700">
                      {t('changeRoleModal.adminNotice', 'As an admin, you can submit a role change request. The request will need to be approved by an owner.')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Role Selection */}
            {!success && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('changeRoleModal.newRole', 'New Role')}
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value as any)}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  <option value="owner">{t('roles.owner', 'Owner')}</option>
                  <option value="admin">{t('roles.admin', 'Admin')}</option>
                  <option value="auditor">{t('roles.auditor', 'Auditor')}</option>
                  <option value="security">{t('roles.security', 'Security')}</option>
                  <option value="user">{t('roles.user', 'User')}</option>
                </select>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
            {success ? (
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                {t('changeRoleModal.close', 'Close')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  {t('changeRoleModal.cancel', 'Cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={!canSubmit}
                >
                  {loading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>
                    {loading
                      ? t('changeRoleModal.processing', 'Processing')
                      : actionType === 'direct'
                        ? t('changeRoleModal.changeRole', 'Change Role')
                        : t('changeRoleModal.submitRequest', 'Submit Request')
                    }
                  </span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }
)

ChangeRoleModal.displayName = 'ChangeRoleModal'

export default ChangeRoleModal
